import { ExclamationCircleIcon } from '@heroicons/react/16/solid';
import { twMerge } from 'tailwind-merge';
import React from 'react';
import { COUNTRY_CODES } from '@constants';

interface PhoneNumberInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  label?: string;
  name: string;
  value?: string;
  countryCode: string;
  onCountryCodeChange: (code: string) => void;
  isInvalid?: boolean;
  validationMessage?: string;
  helperText?: string;
  sizeVariant?: 'xs' | 'sm' | 'lg';
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  optional?: boolean;
  orientation?: 'vertical' | 'horizontal';
}

export function PhoneNumberInput({
  label,
  name,
  value,
  countryCode,
  onCountryCodeChange,
  isInvalid = false,
  validationMessage = '',
  helperText = '',
  sizeVariant = 'sm',
  onChange,
  optional,
  orientation = 'vertical',
  disabled,
  ...props
}: PhoneNumberInputProps) {
  const isHorizontal = orientation === 'horizontal';

  const sizeClasses = {
    xs: 'h-7 text-xs',
    sm: 'h-9 text-sm',
    lg: 'h-11 text-base',
  };

  return (
    <div
      className={twMerge(
        'grow',
        isHorizontal ? 'flex items-center space-x-4' : 'flex flex-col'
      )}
    >
      {label && (
        <div className={isHorizontal ? 'flex flex-col' : 'flex items-center'}>
          <label
            htmlFor={name}
            className={twMerge(
              'block text-sm leading-5 font-medium text-gray-900 font-inter',
              disabled && 'text-gray-400'
            )}
          >
            {label}
          </label>
          {optional && (
            <span
              className={twMerge(
                'text-sm leading-5 font-normal font-inter text-gray-400',
                isHorizontal ? '' : 'ml-2'
              )}
            >
              Optional
            </span>
          )}
        </div>
      )}
      <div
        className={twMerge(
          'relative',
          label && !isHorizontal ? 'mt-2' : '',
          isHorizontal ? 'flex-1 flex flex-col' : ''
        )}
      >
        <div
          className={twMerge(
            'flex rounded-lg bg-white border transition-colors',
            isInvalid
              ? 'border-red-300 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-500/20'
              : 'border-gray-300 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20',
            disabled && 'bg-gray-50 cursor-not-allowed'
          )}
        >
          {/* Country Code Select */}
          <div className='relative flex items-center'>
            <select
              value={countryCode}
              onChange={(e) => onCountryCodeChange(e.target.value)}
              disabled={disabled}
              className={twMerge(
                'rounded-l-lg border-0 bg-transparent py-1.5 pl-3 pr-2 text-gray-700 font-medium focus:outline-none focus:ring-0',
                sizeClasses[sizeVariant],
                disabled && 'cursor-not-allowed text-gray-400'
              )}
              aria-label='Country code'
            >
              {COUNTRY_CODES.map((opt) => (
                <option key={opt.country_code} value={opt.country_code}>
                  {opt.country_code} +{opt.phone_code}
                </option>
              ))}
            </select>
          </div>

          {/* Divider */}
          <div
            className={twMerge(
              'w-px self-stretch my-2',
              isInvalid ? 'bg-red-200' : 'bg-gray-200'
            )}
          />

          {/* Phone Number Input */}
          <input
            id={name}
            name={name}
            type='tel'
            value={value}
            disabled={disabled}
            aria-invalid={isInvalid}
            aria-describedby={isInvalid ? `${name}-error` : undefined}
            onChange={onChange}
            className={twMerge(
              'flex-1 min-w-0 rounded-r-lg border-0 bg-transparent px-3 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0',
              sizeClasses[sizeVariant],
              isInvalid && 'text-red-900 placeholder:text-red-300',
              disabled && 'cursor-not-allowed text-gray-400'
            )}
            {...props}
          />

          {/* Error Icon */}
          {isInvalid && (
            <span className='pointer-events-none flex items-center pr-3'>
              <ExclamationCircleIcon
                aria-hidden='true'
                className='h-5 w-5 text-red-500'
              />
            </span>
          )}
        </div>

        {/* Validation Message */}
        {isInvalid && validationMessage && (
          <p id={`${name}-error`} className='mt-1.5 text-sm text-red-600'>
            {validationMessage}
          </p>
        )}

        {/* Helper Text */}
        {helperText && !isInvalid && (
          <p className='mt-1.5 text-sm text-gray-500'>{helperText}</p>
        )}
      </div>
    </div>
  );
}
